/**
 * O que faz: Seção "Resultados" da aba Follow-up — filtro de período, 4 indicadores principais,
 *            situação da fila (bloqueados, na fila, erros, encerradas por resposta), leituras
 *            automáticas, gráficos e o desempenho de cada campanha.
 * Onde usa: FollowupManagement, acima da lista de campanhas.
 * Conexões: TimeRangeFilter, useFollowupAnalytics, FollowupAnalyticsCharts, FollowupDispatchedLeadsDialog
 *           e FollowupBlockedExecutionsDialog. Visual do Gens Design System (.gens-escopo).
 * Tabelas: Nenhuma direta no componente; dados vêm da RPC followup_analytics e dos hooks de execuções.
 * Data: 15/05/2026 (redesenho no design system em 07/10/2026).
 */

import { useMemo, useState, type ReactNode } from 'react';
import { endOfDay, startOfDay } from 'date-fns';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Clock,
  Hourglass,
  Lightbulb,
  MessageSquare,
  RotateCcw,
  ShieldAlert,
  UserX,
  Users,
  WifiOff,
  type LucideIcon,
} from 'lucide-react';
import { TimeRangeFilter } from '@/components/admin/TimeRangeFilter';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  FILTRO_RESPOSTA_FOLLOWUP,
  formatarDuracaoFollowup,
  mapCampanhasParaMetricas,
  montarInsightsFollowup,
  type FiltroRespostaFollowup,
} from '@/features/followup/analytics';
import { useFollowupDispatchedLeads } from '@/features/followup/dispatchedLeads';
import { useFollowupBloqueados, useFollowupWhatsappDesconectado } from '@/features/followup/blockedExecutions';
import { useFollowupAnalytics } from '@/hooks/useFollowupAnalytics';
import { cn } from '@/lib/utils';
import { FollowupBlockedExecutionsDialog } from './FollowupBlockedExecutionsDialog';
import { FollowupAnalyticsCharts } from './FollowupAnalyticsCharts';
import { FollowupDispatchedLeadsDialog } from './FollowupDispatchedLeadsDialog';

const numberFormatter = new Intl.NumberFormat('pt-BR');
const percentFormatter = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });

// "Máximo" no filtro de período vem sem datas; a RPC exige início e fim, então usamos um início
// anterior a qualquer campanha. Motivo: antes o filtro "Máximo" mostrava tudo zerado.
const INICIO_HISTORICO = new Date(2024, 0, 1);

// Ícones das leituras automáticas, pelo id gerado em montarInsightsFollowup.
const ICONE_INSIGHT: Record<string, LucideIcon> = {
  'taxa-leads': CheckCircle2,
  'tempo-mediana': Clock,
  'melhor-hora': Lightbulb,
  'funil-etapa-1': MessageSquare,
};

interface FollowupCampaignMetricsProps {
  clinicaId?: string | null;
}

export const FollowupCampaignMetrics = ({ clinicaId }: FollowupCampaignMetricsProps = {}) => {
  const [startDate, setStartDate] = useState<Date | null>(() => startOfDay(new Date()));
  const [endDate, setEndDate] = useState<Date | null>(() => endOfDay(new Date()));
  const [currentFilter, setCurrentFilter] = useState('Hoje');
  const [isDispatchedLeadsOpen, setIsDispatchedLeadsOpen] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);
  const [filtroResposta, setFiltroResposta] = useState<FiltroRespostaFollowup>(FILTRO_RESPOSTA_FOLLOWUP.todos);

  // Período efetivo enviado à RPC e às listas (o "Máximo" vira histórico completo até agora).
  const inicioConsulta = startDate ?? INICIO_HISTORICO;
  const fimConsulta = endDate ?? endOfDay(new Date());

  const { data: analytics, isLoading, isPlaceholderData, isFetching, error, refetch } = useFollowupAnalytics({
    startDate: inicioConsulta,
    endDate: fimConsulta,
    clinicaId,
  });
  // Enquanto a primeira resposta não chega, o hook devolve um payload vazio (placeholder):
  // tratamos como carregamento para não piscar zeros nem esconder a seção.
  const carregando = isLoading || Boolean(isPlaceholderData);
  const metrics = useMemo(
    () => [...mapCampanhasParaMetricas(analytics?.campanhas ?? [])]
      .sort((primeira, segunda) => segunda.leads_enviados - primeira.leads_enviados || Number(segunda.ativo) - Number(primeira.ativo)),
    [analytics?.campanhas],
  );
  const resumo = analytics?.resumo;
  const insights = useMemo(() => (analytics ? montarInsightsFollowup(analytics) : []), [analytics]);

  const campaignIds = useMemo(() => metrics.map((metric) => metric.campaign_id), [metrics]);
  const selectedCampaign = metrics.find((metric) => metric.campaign_id === selectedCampaignId) ?? null;
  const { data: dispatchedLeads = [], isLoading: dispatchedLeadsLoading } = useFollowupDispatchedLeads({
    enabled: isDispatchedLeadsOpen,
    startDate: inicioConsulta,
    endDate: fimConsulta,
    campaignIds,
    campaignId: selectedCampaignId,
  });

  // Follow-ups cancelados pelas regras (24h, atraso, atendente, etapa, venda) no período escolhido.
  const [isBloqueadosOpen, setIsBloqueadosOpen] = useState(false);
  const { data: bloqueados = [], isLoading: bloqueadosLoading } = useFollowupBloqueados({
    startDate: inicioConsulta,
    endDate: fimConsulta,
    campaignIds,
  });
  // Aviso de WhatsApp desconectado: o processor segura os envios até a conexão voltar.
  const { data: desconectadoDesde } = useFollowupWhatsappDesconectado(campaignIds);

  const openDispatchedLeads = (
    campaignId: string | null,
    filtro: FiltroRespostaFollowup = FILTRO_RESPOSTA_FOLLOWUP.todos,
  ) => {
    setSelectedCampaignId(campaignId);
    setFiltroResposta(filtro);
    setIsDispatchedLeadsOpen(true);
  };

  const handleFilterChange = (newStartDate: Date | null, newEndDate: Date | null, filterName: string) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setCurrentFilter(filterName);
  };

  // Sem nenhuma campanha na clínica não há o que medir; a lista abaixo mostra o estado vazio.
  if (!carregando && !error && metrics.length === 0) {
    return null;
  }

  const leadsEnviados = resumo?.leads_enviados ?? 0;
  const taxaResposta = resumo?.taxa_resposta_leads ?? 0;
  const semResposta = resumo?.leads_sem_resposta ?? 0;
  const pendentes = resumo?.pendentes ?? 0;
  const erros = resumo?.erros ?? 0;
  const encerradasPorResposta = resumo?.canceladas_resposta ?? 0;
  const temSituacao = bloqueados.length > 0 || pendentes > 0 || erros > 0 || encerradasPorResposta > 0;

  return (
    <section aria-labelledby="titulo-resultados" className="space-y-4">
      {/* Cabeçalho da seção: título à esquerda, período e atalho para os leads à direita. */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 id="titulo-resultados" className="text-lg leading-snug text-foreground">Resultados</h2>
          <p className="mt-0.5 text-[13px] text-muted-foreground">
            Como os leads responderam aos follow-ups · {currentFilter}
            {isFetching && !carregando && <span className="ml-1.5 animate-pulse motion-reduce:animate-none">· atualizando...</span>}
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          {/* O filtro já define a própria largura (280 px no desktop, inteira no celular). */}
          <div className="min-w-0">
            <TimeRangeFilter onFilterChange={handleFilterChange} currentFilter={currentFilter} />
          </div>
          <Button
            type="button"
            variant="outline"
            className="h-11 rounded-full px-4 font-semibold"
            onClick={() => openDispatchedLeads(null)}
          >
            <MessageSquare className="h-4 w-4" />
            Ver leads disparados
          </Button>
        </div>
      </div>

      {desconectadoDesde && (
        <div role="alert" className="flex items-start gap-3 rounded-2xl bg-danger-subtle px-4 py-3 text-sm text-danger-text">
          <WifiOff className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <p>
            <span className="font-semibold">WhatsApp desconectado.</span>{' '}
            Os follow-ups estão em espera e voltam a sair sozinhos quando a conexão for refeita. Reconecte o WhatsApp nas configurações.
          </p>
        </div>
      )}

      {error ? (
        <div role="alert" className="flex flex-col gap-3 rounded-3xl bg-danger-subtle p-5 text-danger-text sm:flex-row sm:items-center sm:justify-between">
          <p className="flex items-start gap-2 text-sm">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            Não foi possível carregar os resultados deste período. {error.message}
          </p>
          <Button type="button" variant="outline" className="h-10 rounded-full bg-card px-4" onClick={() => refetch()}>
            <RotateCcw className="h-4 w-4" />
            Tentar de novo
          </Button>
        </div>
      ) : carregando ? (
        // Esqueletos com a altura final: a página não "pula" quando os números chegam.
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" aria-busy="true" aria-label="Carregando resultados">
          {[0, 1, 2, 3].map((indice) => <Skeleton key={indice} className="h-[132px] rounded-3xl bg-secondary" />)}
        </div>
      ) : (
        <div className="space-y-4 animate-in fade-in-0 duration-300 motion-reduce:animate-none">
          {/* 4 indicadores principais (seção 9.8). Os clicáveis abrem a lista de leads já filtrada. */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <IndicadorFollowup
              icone={Users}
              rotulo="Leads disparados"
              valor={numberFormatter.format(leadsEnviados)}
              detalhe={`${numberFormatter.format(resumo?.mensagens_enviadas ?? 0)} mensagens enviadas`}
              onClick={() => openDispatchedLeads(null, FILTRO_RESPOSTA_FOLLOWUP.todos)}
            />
            <IndicadorFollowup
              icone={CheckCircle2}
              rotulo="Responderam"
              valor={numberFormatter.format(resumo?.leads_respondidos ?? 0)}
              detalhe={(
                <span className="block space-y-1.5">
                  <span className="block font-semibold text-success-text">{percentFormatter.format(taxaResposta)}% dos leads</span>
                  {/* Barra de progresso da seção 9.18: trilho musgo, preenchimento lima profundo. */}
                  <span className="block h-1.5 overflow-hidden rounded-full bg-secondary" aria-hidden="true">
                    <span className="block h-full rounded-full bg-[#8fb83a]" style={{ width: `${Math.min(100, taxaResposta)}%` }} />
                  </span>
                </span>
              )}
              onClick={() => openDispatchedLeads(null, FILTRO_RESPOSTA_FOLLOWUP.respondidos)}
            />
            <IndicadorFollowup
              icone={UserX}
              rotulo="Sem resposta"
              valor={numberFormatter.format(semResposta)}
              detalhe={leadsEnviados > 0 ? `${percentFormatter.format((semResposta / leadsEnviados) * 100)}% ainda não responderam` : 'Nenhum envio no período'}
              onClick={() => openDispatchedLeads(null, FILTRO_RESPOSTA_FOLLOWUP.sem_resposta)}
            />
            <IndicadorFollowup
              icone={Clock}
              rotulo="Tempo de resposta"
              valor={formatarDuracaoFollowup(resumo?.tempo_resposta_mediana_minutos)}
              detalhe="Metade das respostas chegou até aqui"
            />
          </div>

          {/* Situação da fila: estado sempre com ícone e texto (nunca só cor). */}
          {temSituacao && (
            <div className="flex flex-wrap gap-2" aria-label="Situação da fila de envios">
              {bloqueados.length > 0 && (
                <button
                  type="button"
                  onClick={() => setIsBloqueadosOpen(true)}
                  className="inline-flex min-h-9 items-center gap-1.5 rounded-full bg-warning-subtle px-3 text-[13px] font-medium text-warning-text transition-shadow duration-150 hover:shadow-[var(--lg-sombra-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <ShieldAlert className="h-3.5 w-3.5" aria-hidden="true" />
                  {numberFormatter.format(bloqueados.length)} bloqueados pelas regras
                  <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              )}
              {pendentes > 0 && (
                <span className="inline-flex min-h-9 items-center gap-1.5 rounded-full bg-secondary px-3 text-[13px] text-muted-foreground">
                  <Hourglass className="h-3.5 w-3.5" aria-hidden="true" />
                  {numberFormatter.format(pendentes)} na fila para envio
                </span>
              )}
              {erros > 0 && (
                <span className="inline-flex min-h-9 items-center gap-1.5 rounded-full bg-danger-subtle px-3 text-[13px] text-danger-text">
                  <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                  {numberFormatter.format(erros)} com erro no envio
                </span>
              )}
              {encerradasPorResposta > 0 && (
                <span className="inline-flex min-h-9 items-center gap-1.5 rounded-full bg-success-subtle px-3 text-[13px] text-success-text">
                  <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                  {numberFormatter.format(encerradasPorResposta)} sequências pararam porque o lead voltou a falar
                </span>
              )}
            </div>
          )}

          {/* Leituras automáticas do período, em frases curtas. */}
          {insights.length > 0 && (
            <div className="rounded-3xl bg-muted p-4 sm:p-5">
              <p className="lg-rotulo mb-3">O que os números dizem</p>
              <ul className="grid gap-3 sm:grid-cols-2">
                {insights.map((insight) => {
                  const Icone = ICONE_INSIGHT[insight.id] ?? Lightbulb;
                  return (
                    <li key={insight.id} className="flex items-start gap-3 text-[13px] leading-relaxed text-foreground">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-card text-accent-foreground">
                        <Icone className="h-4 w-4" aria-hidden="true" />
                      </span>
                      <span>
                        <strong className="font-semibold">{insight.titulo}.</strong> {insight.texto}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {analytics && <FollowupAnalyticsCharts analytics={analytics} />}

          {/* Desempenho por campanha (seção 9.9 em formato de lista): taxa com barra e atalho para os leads. */}
          <div className="rounded-3xl border border-border bg-card p-5 shadow-[var(--lg-sombra-1)]">
            <div className="mb-2">
              <h3 className="text-[15px] leading-snug text-foreground">Por campanha</h3>
              <p className="mt-0.5 text-xs text-muted-foreground">Ordenado pela quantidade de leads que receberam follow-up no período.</p>
            </div>
            <ul className="divide-y divide-border">
              {metrics.map((metric) => {
                const semEnvio = metric.leads_enviados === 0;
                return (
                  <li
                    key={metric.campaign_id}
                    className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-2 py-3 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_96px_auto]"
                  >
                    <div className="min-w-0">
                      <p className="flex items-center gap-2 text-sm font-medium text-foreground">
                        <span
                          aria-hidden="true"
                          className={cn('h-2 w-2 shrink-0 rounded-full', metric.ativo ? 'bg-success' : 'bg-muted-foreground/50')}
                        />
                        <span className="truncate">{metric.campaign_nome}</span>
                        <span className="sr-only">{metric.ativo ? '(ativa)' : '(pausada)'}</span>
                      </p>
                      <p className="mt-0.5 pl-4 text-xs tabular-nums text-muted-foreground">
                        {semEnvio
                          ? 'Sem envios no período'
                          : `${numberFormatter.format(metric.leads_enviados)} leads · ${numberFormatter.format(metric.enviadas)} mensagens`}
                      </p>
                    </div>

                    {/* Taxa de resposta com barra; no celular fica na segunda linha, ocupando a largura. */}
                    <div className="order-last col-span-2 flex items-center gap-3 sm:order-none sm:col-span-1">
                      <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-secondary" aria-hidden="true">
                        <span className="block h-full rounded-full bg-[#8fb83a]" style={{ width: `${Math.min(100, metric.taxa_resposta_leads)}%` }} />
                      </span>
                      <span className="w-24 text-right text-xs tabular-nums text-foreground">
                        <strong className="font-semibold">{percentFormatter.format(metric.taxa_resposta_leads)}%</strong>
                        <span className="text-muted-foreground"> resp.</span>
                      </span>
                    </div>

                    <span className="hidden text-right text-xs tabular-nums text-muted-foreground sm:block" title="Tempo mediano até a resposta">
                      {formatarDuracaoFollowup(metric.tempo_resposta_mediana_minutos)}
                    </span>

                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-9 rounded-full px-3 text-[13px] font-semibold text-accent-foreground"
                      disabled={semEnvio}
                      onClick={() => openDispatchedLeads(metric.campaign_id)}
                      aria-label={`Ver leads de ${metric.campaign_nome}`}
                    >
                      Ver leads
                      <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                    </Button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      <FollowupBlockedExecutionsDialog
        open={isBloqueadosOpen}
        onOpenChange={setIsBloqueadosOpen}
        bloqueados={bloqueados}
        isLoading={bloqueadosLoading}
      />

      <FollowupDispatchedLeadsDialog
        open={isDispatchedLeadsOpen}
        onOpenChange={setIsDispatchedLeadsOpen}
        leads={dispatchedLeads}
        isLoading={dispatchedLeadsLoading}
        isAdminMode={Boolean(clinicaId)}
        targetClinicaId={clinicaId}
        campaignName={selectedCampaign?.campaign_nome ?? null}
        filtroResposta={filtroResposta}
        onFiltroRespostaChange={setFiltroResposta}
      />
    </section>
  );
};

interface IndicadorFollowupProps {
  icone: LucideIcon;
  rotulo: string;
  valor: string;
  detalhe: ReactNode;
  onClick?: () => void;
}

/**
 * KPI da seção 9.8: rótulo DM Sans 13 px, valor Sora 700 e uma linha de contexto.
 * Quando clicável vira botão inteiro (card interativo), com seta indicando que abre a lista.
 */
const IndicadorFollowup = ({ icone: Icone, rotulo, valor, detalhe, onClick }: IndicadorFollowupProps) => {
  const conteudo = (
    <>
      <span className="flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-2 text-[13px] font-medium text-muted-foreground">
          {/* No celular (2 colunas estreitas) o ícone some para o rótulo caber inteiro. */}
          <span className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-secondary text-accent-foreground sm:flex">
            <Icone className="h-4 w-4" aria-hidden="true" />
          </span>
          <span className="leading-tight">{rotulo}</span>
        </span>
        {onClick && (
          <ChevronRight
            className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-hover:translate-x-0.5 motion-reduce:transition-none"
            aria-hidden="true"
          />
        )}
      </span>
      <span className="mt-3 block font-display text-[26px] font-bold leading-none tracking-tight tabular-nums text-foreground sm:text-[30px]">
        {valor}
      </span>
      <span className="mt-2 block text-xs leading-relaxed text-muted-foreground">{detalhe}</span>
    </>
  );

  const classeBase = 'flex h-full w-full flex-col rounded-3xl border border-border bg-card p-4 text-left shadow-[var(--lg-sombra-1)] sm:p-5';

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          classeBase,
          'group transition-[box-shadow,border-color] duration-200 hover:border-input hover:shadow-[var(--lg-sombra-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none',
        )}
      >
        {conteudo}
      </button>
    );
  }

  return <div className={classeBase}>{conteudo}</div>;
};
